/**
 * Faction Demands System
 *
 * Handles generation, resolution, and deadline processing for faction demands.
 * When faction satisfaction drops below 40, factions issue demands based on
 * their ideology and composition. If ignored, factions escalate through:
 * demanding → threatening → defecting.
 *
 * @module lib/factionDemands
 */

import { supabase } from '@/lib/supabase'
import type {
  Faction,
  FactionWithMembers,
  FactionDemandType,
  FactionStatus,
  IdeologyVector,
  Party,
  CandidateRole,
  CabinetRoleDemandDetails,
  PolicyShiftDemandDetails,
  CandidatePromotionDemandDetails,
  BillSupportDemandDetails,
  LeadershipChangeDemandDetails,
  FactionDemandDetails,
  DemandResolution,
  DemandDeadlineResult,
} from '@/types'
import {
  fetchFactionWithMembers,
  setFactionDemand,
  clearFactionDemand,
  updateFactionStatus,
} from '@/services/factions'
import { fetchParty } from '@/services/parties'
import {
  DEMAND_DEADLINES,
  DEMAND_SATISFACTION_CHANGES,
  DEMAND_GENERATION_THRESHOLDS,
  IDEOLOGY_AXIS_NAMES,
  BILL_CATEGORIES,
} from '@/constants/factionDemands'
import { SATISFACTION_THRESHOLDS } from '@/constants/factionSatisfaction'

// ============================================================================
// Demand Generation
// ============================================================================

/**
 * Checks if a faction should generate a new demand.
 *
 * Conditions:
 * 1. Satisfaction below threshold (40)
 * 2. No active demand already exists
 * 3. Faction is not dormant or defecting
 *
 * @param faction - The faction to check
 * @returns True if faction should generate a demand
 */
export function shouldGenerateDemand(faction: Faction): boolean {
  // Already has a demand
  if (faction.demand_type !== null) {
    return false
  }

  // Check satisfaction threshold
  if (faction.satisfaction >= DEMAND_GENERATION_THRESHOLDS.SATISFACTION_THRESHOLD) {
    return false
  }

  // Check status - dormant factions don't make demands, defecting factions are past demands
  if (faction.status === 'dormant' || faction.status === 'defecting') {
    return false
  }

  return true
}

/**
 * Determines the appropriate demand type based on faction state and party context.
 *
 * Priority order:
 * 1. cabinet_role - If no faction members in cabinet AND influence > 30
 * 2. candidate_promotion - If high-recognition member without significant role
 * 3. policy_shift - If significant ideology divergence on any axis
 * 4. leadership_change - If satisfaction critical (<15) and influence > 50 (20% chance)
 * 5. Fallback: policy_shift on most divergent axis
 *
 * @param faction - The faction with member data
 * @param party - The party the faction belongs to
 * @returns The determined demand type
 */
export function determineDemandType(
  faction: FactionWithMembers,
  party: Party
): FactionDemandType {
  const factionIdeology = faction.ideology_vector
  const partyIdeology = party.ideology_vector

  if (!factionIdeology) {
    // No ideology vector - default to policy shift
    return 'policy_shift'
  }

  // Get faction members' current roles
  const memberRoles = faction.members.map((m) => m.candidate.role)
  const hasMinister = memberRoles.includes('minister')

  // Find highest recognition member without significant role
  const unassignedMembers = faction.members
    .filter((m) => m.candidate.role === 'none' || m.candidate.role === 'list_candidate')
    .sort((a, b) => b.candidate.recognition - a.candidate.recognition)

  // Calculate ideology divergence per axis
  const divergences = factionIdeology.map((factionPos, axis) => ({
    axis,
    divergence: factionPos - partyIdeology[axis],
    absoluteDivergence: Math.abs(factionPos - partyIdeology[axis]),
  }))

  const maxDivergence = divergences.reduce((max, curr) =>
    curr.absoluteDivergence > max.absoluteDivergence ? curr : max
  )

  // Decision logic

  // 1. Cabinet role if no ministers and influential
  if (!hasMinister && faction.influence > DEMAND_GENERATION_THRESHOLDS.CABINET_ROLE_MIN_INFLUENCE) {
    return 'cabinet_role'
  }

  // 2. Candidate promotion if high-recognition member is unassigned
  if (
    unassignedMembers.length > 0 &&
    unassignedMembers[0].candidate.recognition >
      DEMAND_GENERATION_THRESHOLDS.CANDIDATE_PROMOTION_MIN_RECOGNITION
  ) {
    return 'candidate_promotion'
  }

  // 3. Policy shift if significant ideological divergence
  if (maxDivergence.absoluteDivergence > DEMAND_GENERATION_THRESHOLDS.POLICY_SHIFT_MIN_DIVERGENCE) {
    return 'policy_shift'
  }

  // 4. Leadership change only at critical satisfaction and high influence
  if (
    faction.satisfaction < DEMAND_GENERATION_THRESHOLDS.LEADERSHIP_CHANGE_MAX_SATISFACTION &&
    faction.influence > DEMAND_GENERATION_THRESHOLDS.LEADERSHIP_CHANGE_MIN_INFLUENCE
  ) {
    // 20% chance of leadership demand at critical levels
    if (Math.random() < DEMAND_GENERATION_THRESHOLDS.LEADERSHIP_CHANGE_PROBABILITY) {
      return 'leadership_change'
    }
  }

  // 5. Fallback to policy shift
  return 'policy_shift'
}

/**
 * Generates specific demand details based on type and faction state.
 *
 * @param demandType - The type of demand to generate
 * @param faction - The faction with member data
 * @param party - The party the faction belongs to
 * @returns The demand details
 */
export function generateDemandDetails(
  demandType: FactionDemandType,
  faction: FactionWithMembers,
  party: Party
): FactionDemandDetails {
  switch (demandType) {
    case 'cabinet_role':
      return generateCabinetRoleDemandDetails(faction)

    case 'policy_shift':
      return generatePolicyShiftDemandDetails(faction, party)

    case 'candidate_promotion':
      return generateCandidatePromotionDemandDetails(faction)

    case 'bill_support':
      return generateBillSupportDemandDetails(faction)

    case 'leadership_change':
      return generateLeadershipChangeDemandDetails(faction)

    default:
      throw new Error(`Unknown demand type: ${demandType}`)
  }
}

/**
 * Main entry point for demand generation.
 * Fetches faction and party, determines demand type, generates details,
 * and updates the faction record.
 *
 * @param factionId - The ID of the faction
 * @param currentTick - The current game tick
 * @returns The updated faction, or null if demand should not be generated
 */
export async function generateFactionDemand(
  factionId: string,
  currentTick: number
): Promise<Faction | null> {
  // Fetch faction with members
  const faction = await fetchFactionWithMembers(factionId)
  if (!faction) {
    throw new Error(`Faction not found: ${factionId}`)
  }

  // Check if should generate demand
  if (!shouldGenerateDemand(faction)) {
    return null
  }

  // Fetch party
  const party = await fetchParty(faction.party_id)

  // Determine demand type
  const demandType = determineDemandType(faction, party)

  // Generate demand details
  const demandDetails = generateDemandDetails(demandType, faction, party)

  // Set the demand on the faction
  const updatedFaction = await setFactionDemand(
    factionId,
    demandType,
    demandDetails as unknown as Record<string, unknown>,
    currentTick
  )

  // Update status to demanding if not already
  if (updatedFaction.status !== 'demanding' && updatedFaction.status !== 'threatening') {
    await updateFactionStatus(factionId, 'demanding')
  }

  return updatedFaction
}

// ============================================================================
// Demand Detail Generators (Private)
// ============================================================================

/**
 * Generate cabinet role demand details.
 * Ranks faction members by suitability (admin_skill * 2 + recognition).
 */
function generateCabinetRoleDemandDetails(faction: FactionWithMembers): CabinetRoleDemandDetails {
  // Rank faction members by suitability for cabinet
  const rankedCandidates = [...faction.members]
    .sort((a, b) => {
      // Note: admin_skill is not in the joined candidate data, so we use recognition as proxy
      const aScore = a.candidate.recognition
      const bScore = b.candidate.recognition
      return bScore - aScore
    })
    .map((m) => m.candidate.id)

  return {
    type: 'cabinet_role',
    preferred_candidates: rankedCandidates.slice(0, 3),
    any_ministry: true,
  }
}

/**
 * Generate policy shift demand details.
 * Finds the axis with largest party-faction divergence.
 */
function generatePolicyShiftDemandDetails(
  faction: FactionWithMembers,
  party: Party
): PolicyShiftDemandDetails {
  const factionIdeology = faction.ideology_vector
  const partyIdeology = party.ideology_vector

  // Find most divergent axis
  let maxAxis = 0
  let maxDivergence = 0

  if (factionIdeology) {
    for (let axis = 0; axis < 7; axis++) {
      const divergence = Math.abs(factionIdeology[axis] - partyIdeology[axis])
      if (divergence > maxDivergence) {
        maxDivergence = divergence
        maxAxis = axis
      }
    }
  }

  const factionPosition = factionIdeology ? factionIdeology[maxAxis] : 0
  const partyPosition = partyIdeology[maxAxis]
  const direction = factionPosition > partyPosition ? 'increase' : 'decrease'

  // Minimum shift: 15 points or 1/3 of the gap, whichever is smaller
  const minimumShift = Math.min(15, Math.floor(maxDivergence / 3))

  return {
    type: 'policy_shift',
    axis: maxAxis,
    axis_name: IDEOLOGY_AXIS_NAMES[maxAxis],
    direction,
    current_party_position: partyPosition,
    faction_position: factionPosition,
    minimum_shift: Math.max(5, minimumShift), // At least 5 points
  }
}

/**
 * Generate candidate promotion demand details.
 * Finds highest recognition unassigned member.
 */
function generateCandidatePromotionDemandDetails(
  faction: FactionWithMembers
): CandidatePromotionDemandDetails {
  // Find highest recognition unassigned member
  const unassigned = faction.members
    .filter((m) => m.candidate.role === 'none' || m.candidate.role === 'list_candidate')
    .sort((a, b) => b.candidate.recognition - a.candidate.recognition)

  if (unassigned.length === 0) {
    // Fallback: use any member
    const anyMember = faction.members[0]
    return {
      type: 'candidate_promotion',
      candidate_id: anyMember.candidate.id,
      candidate_name: anyMember.candidate.name,
      acceptable_roles: ['regional_candidate', 'list_candidate', 'chair'],
    }
  }

  const candidate = unassigned[0].candidate

  return {
    type: 'candidate_promotion',
    candidate_id: candidate.id,
    candidate_name: candidate.name,
    acceptable_roles: ['regional_candidate', 'list_candidate', 'chair'],
  }
}

/**
 * Generate bill support demand details.
 * Finds bill category matching faction's strongest ideology position.
 */
function generateBillSupportDemandDetails(faction: FactionWithMembers): BillSupportDemandDetails {
  const factionIdeology = faction.ideology_vector

  // Find axis where faction has strongest position
  let strongestAxis = 0
  let strongestPosition = 0

  if (factionIdeology) {
    for (let axis = 0; axis < 7; axis++) {
      if (Math.abs(factionIdeology[axis]) > Math.abs(strongestPosition)) {
        strongestPosition = factionIdeology[axis]
        strongestAxis = axis
      }
    }
  }

  const billCategory = BILL_CATEGORIES[strongestAxis]
  const stance = strongestPosition > 0 ? 'support' : 'oppose'

  return {
    type: 'bill_support',
    bill_category: billCategory,
    stance,
    ideology_axis: strongestAxis,
    reasoning: `Faction demands party ${stance} ${billCategory} legislation aligned with their values`,
  }
}

/**
 * Generate leadership change demand details.
 * Identifies faction grievance and potential replacements.
 */
function generateLeadershipChangeDemandDetails(
  faction: FactionWithMembers
): LeadershipChangeDemandDetails {
  // Rank faction members as potential leaders
  const potentialLeaders = [...faction.members]
    .sort((a, b) => {
      const aScore = a.candidate.recognition + a.commitment_level
      const bScore = b.candidate.recognition + b.commitment_level
      return bScore - aScore
    })
    .slice(0, 2)
    .map((m) => m.candidate.id)

  return {
    type: 'leadership_change',
    reason: 'Faction has lost confidence in current party leadership',
    preferred_successor_ids: potentialLeaders,
  }
}

// ============================================================================
// Demand Resolution
// ============================================================================

/**
 * Check if a cabinet role demand has been met.
 *
 * Met: A preferred faction member was appointed
 * Partially met: Any faction member was appointed (not preferred)
 * Not met: No faction member appointed
 *
 * @param faction - The faction with members
 * @param demandDetails - The cabinet role demand details
 * @param newCabinetAppointment - Optional info about a new cabinet appointment
 * @returns The resolution status
 */
export function checkCabinetRoleDemandMet(
  faction: FactionWithMembers,
  demandDetails: CabinetRoleDemandDetails,
  newCabinetAppointment?: { candidateId: string; ministryName: string }
): DemandResolution {
  if (!newCabinetAppointment) {
    return {
      status: 'not_met',
      satisfactionChange: 0,
      message: 'No cabinet changes',
    }
  }

  const factionMemberIds = new Set(faction.members.map((m) => m.candidate.id))

  if (factionMemberIds.has(newCabinetAppointment.candidateId)) {
    // A faction member was appointed
    const isPreferred = demandDetails.preferred_candidates.includes(
      newCabinetAppointment.candidateId
    )

    if (isPreferred) {
      return {
        status: 'met',
        satisfactionChange: DEMAND_SATISFACTION_CHANGES.MET,
        message: `${newCabinetAppointment.ministryName} position granted to faction member`,
      }
    } else {
      return {
        status: 'partially_met',
        satisfactionChange: DEMAND_SATISFACTION_CHANGES.PARTIALLY_MET,
        message: `Cabinet position granted to faction member (not preferred candidate)`,
      }
    }
  }

  return {
    status: 'not_met',
    satisfactionChange: 0,
    message: 'Appointed candidate not a faction member',
  }
}

/**
 * Check if a policy shift demand has been met.
 *
 * Met: Party shifted by minimum_shift or more in correct direction
 * Partially met: Party shifted in correct direction but less than minimum
 * Rejected: Party moved significantly in wrong direction
 * Not met: No significant movement
 *
 * @param demandDetails - The policy shift demand details
 * @param oldPartyIdeology - Party ideology before the change
 * @param newPartyIdeology - Party ideology after the change
 * @returns The resolution status
 */
export function checkPolicyShiftDemandMet(
  demandDetails: PolicyShiftDemandDetails,
  oldPartyIdeology: IdeologyVector,
  newPartyIdeology: IdeologyVector
): DemandResolution {
  const axis = demandDetails.axis
  const oldPosition = oldPartyIdeology[axis]
  const newPosition = newPartyIdeology[axis]

  const shift = newPosition - oldPosition
  const correctDirection = demandDetails.direction === 'increase' ? shift > 0 : shift < 0
  const shiftMagnitude = Math.abs(shift)

  if (!correctDirection) {
    if (shiftMagnitude > 5) {
      // Party moved in wrong direction significantly
      return {
        status: 'rejected',
        satisfactionChange: DEMAND_SATISFACTION_CHANGES.REJECTED,
        message: `Party moved ${demandDetails.axis_name} in opposite direction`,
      }
    }
    return {
      status: 'not_met',
      satisfactionChange: 0,
      message: 'No progress on policy shift',
    }
  }

  if (shiftMagnitude >= demandDetails.minimum_shift) {
    return {
      status: 'met',
      satisfactionChange: DEMAND_SATISFACTION_CHANGES.MET,
      message: `Party shifted ${demandDetails.axis_name} by ${shiftMagnitude} points`,
    }
  }

  if (shiftMagnitude >= demandDetails.minimum_shift / 2) {
    return {
      status: 'partially_met',
      satisfactionChange: DEMAND_SATISFACTION_CHANGES.PARTIALLY_MET,
      message: `Party made partial progress on ${demandDetails.axis_name} (${shiftMagnitude} points)`,
    }
  }

  return {
    status: 'not_met',
    satisfactionChange: 0,
    message: 'Insufficient policy shift',
  }
}

/**
 * Check if a candidate promotion demand has been met.
 *
 * Met: Candidate assigned to an acceptable role
 * Partially met: Candidate promoted but not to preferred role
 * Not met: No change to candidate role
 *
 * @param demandDetails - The candidate promotion demand details
 * @param roleChange - Optional info about a role change
 * @returns The resolution status
 */
export function checkCandidatePromotionDemandMet(
  demandDetails: CandidatePromotionDemandDetails,
  roleChange?: { candidateId: string; newRole: CandidateRole }
): DemandResolution {
  if (!roleChange) {
    return {
      status: 'not_met',
      satisfactionChange: 0,
      message: 'No role changes',
    }
  }

  if (roleChange.candidateId !== demandDetails.candidate_id) {
    return {
      status: 'not_met',
      satisfactionChange: 0,
      message: 'Different candidate promoted',
    }
  }

  if (demandDetails.acceptable_roles.includes(roleChange.newRole)) {
    return {
      status: 'met',
      satisfactionChange: DEMAND_SATISFACTION_CHANGES.MET,
      message: `${demandDetails.candidate_name} promoted to ${roleChange.newRole}`,
    }
  }

  // Promoted but to a different role
  if (roleChange.newRole !== 'none' && roleChange.newRole !== 'list_candidate') {
    return {
      status: 'partially_met',
      satisfactionChange: DEMAND_SATISFACTION_CHANGES.PARTIALLY_MET,
      message: `${demandDetails.candidate_name} given role, but not preferred position`,
    }
  }

  return {
    status: 'not_met',
    satisfactionChange: 0,
    message: 'Candidate not promoted',
  }
}

/**
 * Check if a bill support demand has been met.
 *
 * Met: Party voted in demanded direction on matching bill category
 * Partially met: Party abstained
 * Rejected: Party voted opposite to demand
 * Not met: Bill not in relevant category
 *
 * @param demandDetails - The bill support demand details
 * @param partyVote - How the party voted
 * @param billCategory - The category of the bill
 * @returns The resolution status
 */
export function checkBillSupportDemandMet(
  demandDetails: BillSupportDemandDetails,
  partyVote: 'yes' | 'no' | 'abstain',
  billCategory: string
): DemandResolution {
  // Only check bills in the relevant category
  if (billCategory !== demandDetails.bill_category) {
    return {
      status: 'not_met',
      satisfactionChange: 0,
      message: 'Bill not in relevant category',
    }
  }

  const wantedVote = demandDetails.stance === 'support' ? 'yes' : 'no'

  if (partyVote === wantedVote) {
    return {
      status: 'met',
      satisfactionChange: DEMAND_SATISFACTION_CHANGES.MET,
      message: `Party voted to ${demandDetails.stance} ${demandDetails.bill_category} legislation`,
    }
  }

  if (partyVote === 'abstain') {
    return {
      status: 'partially_met',
      satisfactionChange: Math.floor(DEMAND_SATISFACTION_CHANGES.PARTIALLY_MET / 2),
      message: `Party abstained on ${demandDetails.bill_category} legislation`,
    }
  }

  // Voted opposite
  return {
    status: 'rejected',
    satisfactionChange: DEMAND_SATISFACTION_CHANGES.REJECTED,
    message: `Party voted against faction wishes on ${demandDetails.bill_category} legislation`,
  }
}

/**
 * Resolves a demand with the given resolution status.
 * Applies satisfaction change, clears the demand, and updates status.
 *
 * @param factionId - The faction ID
 * @param resolution - The resolution to apply
 * @param _currentTick - The current game tick (reserved for future use)
 * @returns The updated faction
 */
export async function resolveDemand(
  factionId: string,
  resolution: DemandResolution,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _currentTick: number
): Promise<Faction> {
  // Apply satisfaction change
  if (resolution.satisfactionChange !== 0) {
    const { data: faction, error: fetchError } = await supabase
      .from('factions')
      .select('satisfaction')
      .eq('id', factionId)
      .single()

    if (fetchError) {
      throw new Error(`Failed to fetch faction: ${fetchError.message}`)
    }

    const newSatisfaction = Math.max(
      0,
      Math.min(100, faction.satisfaction + resolution.satisfactionChange)
    )

    await supabase.from('factions').update({ satisfaction: newSatisfaction }).eq('id', factionId)
  }

  // Clear the demand
  const updatedFaction = await clearFactionDemand(factionId)

  // Update status based on new satisfaction
  const newStatus = determineStatusFromSatisfaction(
    updatedFaction.satisfaction,
    updatedFaction.status,
    false // No longer has demand
  )

  if (newStatus !== updatedFaction.status) {
    return await updateFactionStatus(factionId, newStatus)
  }

  return updatedFaction
}

/**
 * Explicitly rejects a faction demand.
 * Applies rejection penalty and clears the demand.
 *
 * @param factionId - The faction ID
 * @param currentTick - The current game tick
 * @returns The updated faction
 */
export async function rejectDemand(factionId: string, currentTick: number): Promise<Faction> {
  return resolveDemand(
    factionId,
    {
      status: 'rejected',
      satisfactionChange: DEMAND_SATISFACTION_CHANGES.REJECTED,
      message: 'Demand explicitly rejected by party leadership',
    },
    currentTick
  )
}

// ============================================================================
// Deadline Processing
// ============================================================================

/**
 * Processes demand deadlines for all factions in a nation.
 * Called during tick processing. Escalates factions whose demands have been ignored.
 *
 * @param nationId - The nation ID
 * @param currentTick - The current game tick
 * @returns Array of deadline results
 */
export async function processDemandDeadlines(
  nationId: string,
  currentTick: number
): Promise<DemandDeadlineResult[]> {
  const results: DemandDeadlineResult[] = []

  // Fetch factions with active demands
  const { data: factions, error } = await supabase
    .from('factions')
    .select(
      `
      *,
      party:parties!inner(nation_id)
    `
    )
    .not('demand_type', 'is', null)
    .eq('party.nation_id', nationId)

  if (error) {
    throw new Error(`Failed to fetch factions with demands: ${error.message}`)
  }

  for (const faction of factions || []) {
    if (!faction.last_demand_tick || !faction.demand_type) continue

    const ticksSinceDemand = currentTick - faction.last_demand_tick
    const previousStatus = faction.status as FactionStatus

    let shouldEscalate = false
    let newStatus: FactionStatus = faction.status

    if (
      faction.status === 'demanding' &&
      ticksSinceDemand >= DEMAND_DEADLINES.DEMANDING_TO_THREATENING
    ) {
      // Escalate to threatening
      shouldEscalate = true
      newStatus = 'threatening'
    } else if (
      faction.status === 'threatening' &&
      ticksSinceDemand >=
        DEMAND_DEADLINES.DEMANDING_TO_THREATENING + DEMAND_DEADLINES.THREATENING_TO_DEFECTING
    ) {
      // Escalate to defecting
      shouldEscalate = true
      newStatus = 'defecting'
    }

    if (shouldEscalate) {
      await updateFactionStatus(faction.id, newStatus)

      results.push({
        factionId: faction.id,
        factionName: faction.name,
        partyId: faction.party_id,
        demandType: faction.demand_type as FactionDemandType,
        ticksOverdue:
          ticksSinceDemand -
          (faction.status === 'demanding'
            ? DEMAND_DEADLINES.DEMANDING_TO_THREATENING
            : DEMAND_DEADLINES.DEMANDING_TO_THREATENING + DEMAND_DEADLINES.THREATENING_TO_DEFECTING),
        previousStatus,
        newStatus,
        escalated: true,
      })
    }
  }

  return results
}

/**
 * Gets the number of ticks remaining before deadline escalation.
 *
 * @param faction - The faction to check
 * @param currentTick - The current game tick
 * @returns Ticks remaining (negative if overdue, -1 if no active demand)
 */
export function getDeadlineTicks(faction: Faction, currentTick: number): number {
  if (!faction.last_demand_tick || !faction.demand_type) {
    return -1 // No active demand
  }

  const ticksSinceDemand = currentTick - faction.last_demand_tick

  if (faction.status === 'demanding') {
    return DEMAND_DEADLINES.DEMANDING_TO_THREATENING - ticksSinceDemand
  }

  if (faction.status === 'threatening') {
    const totalDeadline =
      DEMAND_DEADLINES.DEMANDING_TO_THREATENING + DEMAND_DEADLINES.THREATENING_TO_DEFECTING
    return totalDeadline - ticksSinceDemand
  }

  return -1 // Not in a deadline state
}

// ============================================================================
// Message Formatting
// ============================================================================

/**
 * Formats a demand into a human-readable message for UI display.
 *
 * @param demandType - The type of demand
 * @param demandDetails - The demand details
 * @param factionName - The name of the faction
 * @returns Human-readable demand message
 */
export function formatDemandMessage(
  demandType: FactionDemandType,
  demandDetails: FactionDemandDetails,
  factionName: string
): string {
  switch (demandType) {
    case 'cabinet_role':
      return `The ${factionName} demands representation in the cabinet. They expect one of their members to be appointed to a ministry.`

    case 'policy_shift': {
      const details = demandDetails as PolicyShiftDemandDetails
      const directionWord = details.direction === 'increase' ? 'more' : 'less'
      return `The ${factionName} demands the party adopt a ${directionWord} ${details.axis_name.toLowerCase()} stance. They expect a shift of at least ${details.minimum_shift} points.`
    }

    case 'candidate_promotion': {
      const details = demandDetails as CandidatePromotionDemandDetails
      return `The ${factionName} demands that ${details.candidate_name} be given a more prominent role in the party.`
    }

    case 'bill_support': {
      const details = demandDetails as BillSupportDemandDetails
      return `The ${factionName} demands the party ${details.stance} upcoming ${details.bill_category} legislation.`
    }

    case 'leadership_change':
      return `The ${factionName} has lost confidence in party leadership and demands a change at the top.`

    default:
      return `The ${factionName} has issued a demand.`
  }
}

/**
 * Formats an ultimatum message when faction reaches threatening status.
 *
 * @param demandType - The type of demand
 * @param demandDetails - The demand details
 * @param factionName - The name of the faction
 * @param ticksRemaining - Ticks before defection
 * @returns Human-readable ultimatum message
 */
export function formatUltimatumMessage(
  demandType: FactionDemandType,
  demandDetails: FactionDemandDetails,
  factionName: string,
  ticksRemaining: number
): string {
  const baseMessage = formatDemandMessage(demandType, demandDetails, factionName)
  const tickWord = ticksRemaining === 1 ? 'tick' : 'ticks'
  return `ULTIMATUM: ${baseMessage} If not addressed within ${ticksRemaining} ${tickWord}, the faction may defect from the party.`
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Determines faction status based on satisfaction level.
 * Simplified version for use in demand resolution.
 */
function determineStatusFromSatisfaction(
  satisfaction: number,
  currentStatus: FactionStatus,
  hasDemand: boolean
): FactionStatus {
  // Defection is terminal
  if (currentStatus === 'defecting') {
    return 'defecting'
  }

  // Satisfaction-based status determination
  if (satisfaction <= SATISFACTION_THRESHOLDS.ZERO) {
    return 'defecting'
  }

  if (satisfaction < SATISFACTION_THRESHOLDS.CRITICAL) {
    // Below critical - threatening if had demand, otherwise demanding
    if (currentStatus === 'threatening' || currentStatus === 'demanding') {
      return currentStatus
    }
    return 'demanding'
  }

  if (satisfaction < SATISFACTION_THRESHOLDS.LOW) {
    return hasDemand ? 'demanding' : 'active'
  }

  if (satisfaction < SATISFACTION_THRESHOLDS.MEDIUM) {
    return 'active'
  }

  if (satisfaction >= SATISFACTION_THRESHOLDS.HIGH) {
    return hasDemand ? 'active' : 'dormant'
  }

  return 'active'
}
